import { Box, Icon, Typography } from "@outschool/backpack";
import { faComment } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  BlockScopedMessageDraft,
  BlockScopedMessageInput,
  BlockScopedOptimisticThreadCreatedPayload,
  BlockScopedThreadCreateFailedPayload,
} from "@outschool/ui-components-classroom";
import React from "react";

import ThreadViewLayout from "./ThreadViewLayout";
import { BlockScopedPostTarget, ThreadViewContext } from "./types";
import { learnerSenderFromLearner } from "./utils";

type NewThreadViewProps = {
  viewContext: ThreadViewContext;
  postTarget: BlockScopedPostTarget;
  postTitle?: string;
  hideBackButton?: boolean;
  onBack: () => void;
  onClose: () => void;
  onThreadCreated: (threadUid: string) => void;
  onOptimisticThreadCreated?: (
    payload: BlockScopedOptimisticThreadCreatedPayload,
  ) => void;
  onThreadCreateFailed?: (
    payload: BlockScopedThreadCreateFailedPayload,
  ) => void;
  initialDraft?: BlockScopedMessageDraft | null;
  initialError?: unknown;
};

export default function NewThreadView(props: NewThreadViewProps) {
  const {
    viewContext,
    postTarget,
    onBack,
    onClose,
    onThreadCreated,
    onOptimisticThreadCreated,
    onThreadCreateFailed,
    initialDraft,
    initialError,
  } = props;
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\NewThreadView",
  );
  const teacherName = viewContext.leader?.name?.trim() || t("Teacher");
  const normalizedPostTitle = props.postTitle?.trim() ?? "";
  const hasPostTitle = normalizedPostTitle.length > 0;

  return (
    <ThreadViewLayout
      section={viewContext.section}
      leader={viewContext.leader}
      postTitle={
        hasPostTitle
          ? normalizedPostTitle
          : t("Message {{teacherName}}", { teacherName })
      }
      postSubtitle={
        hasPostTitle
          ? t("Ask your teacher a question about this post.")
          : t("Ask anything about the course")
      }
      showBackButton={!props.hideBackButton}
      onBack={onBack}
      onClose={onClose}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 24,
          gap: 5,
        }}
      >
        <Icon
          icon={faComment}
          sx={{
            color: "neutral.400",
            fontSize: "4rem",
            mb: 4,
          }}
        />
        <Typography variant="h6" color="text.secondary">
          {t("Start a conversation")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          {t("Type your question below")}
        </Typography>
      </Box>

      <BlockScopedMessageInput
        placeholder={t("Message your teacher")}
        sectionUid={viewContext.section.uid}
        learnerUid={viewContext.learner.uid}
        {...postTarget}
        sender={learnerSenderFromLearner(viewContext.learner)}
        onThreadCreated={onThreadCreated}
        onOptimisticThreadCreated={onOptimisticThreadCreated}
        onThreadCreateFailed={onThreadCreateFailed}
        initialDraft={initialDraft}
        initialError={initialError}
        sx={{ borderColor: "divider" }}
      />
    </ThreadViewLayout>
  );
}
